import { getActiveProfileTable } from "building-generation/elements/profileTools";
import { getCornerBoardTrims } from "building-query/trims/cornerBoards";
import { getFacadeTrims } from "building-query/trims/facades/facadeTrims";
import type { OpeningTrimRail } from "building-query/trims/openings/openingTrims";
import { getOpeningRails } from "building-query/trims/openings/openingTrims";
import { getPorchHeaders } from "building-query/trims/porches/porchHeaders";
import { getRoofTrims } from "building-query/trims/roof/roofTrims";
import { TrimKeyMeasureCondition, type TrimKeyMeasure } from "building-query/trims/trimTypes";
import type { Building } from "building-types/building/buildingTypes";
import type {
  ExteriorTrimKAM,
  ExteriorTrimKAMOptions,
  ExteriorTrimAttributes,
  ExteriorTrimMeasures,
} from "building-types/dataSequences/builtin/ExteriorTrimKAM";
import type {
  OpeningCasingKAM,
  OpeningCasingKAMOptions,
  OpeningCasingAttributes,
  OpeningCasingMeasures,
} from "building-types/dataSequences/builtin/OpeningCasingKAM";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { keyAttributeNew, keyMeasureNew, keyMeasureOptionsNew } from "doc/utils/dataSequenceTools";

export type ExteriorTrimSubEntity = "EXTERIOR_TRIM";

interface TrimKBMData extends TrimKeyMeasure {
  profile?: string;
}

/** @deprecated - use the Building Query API instead */
function getTrimFromBuilding(building: Building): TrimKBMData[] {
  const {
    settings: { profile },
    enabledFeatures,
    doc: { profiles },
  } = building;
  const trimEntries: TrimKBMData[] = [];
  const profileTable = getActiveProfileTable({
    settingsToCatalogs: enabledFeatures.settingsToCatalogs,
    profiles,
    profileSettings: profile,
  });

  getPorchHeaders(building, trimEntries);
  getCornerBoardTrims(building, trimEntries);
  getRoofTrims(building, trimEntries);
  getFacadeTrims(building, trimEntries);

  return trimEntries.map((trim) => {
    trim.profile = trim.profileId ? profileTable[trim.profileId]?.name ?? "DEFAULT_PROFILE" : "DEFAULT_PROFILE";
    return trim;
  });
}

function makeExteriorTrimKAM(trim: TrimKBMData): ExteriorTrimKAM {
  return {
    type: "EXTERIOR_TRIM",
    attributes: {
      type: keyAttributeNew(trim.category),
      name: keyAttributeNew(trim.name),
      profile: keyAttributeNew(trim.profile ?? ""),
      entityName: keyAttributeNew(`${trim.category}-${trim.profileId}-${trim.name}`),
    },
    measures: {
      length: keyMeasureNew(trim.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: trim.handles ? [...trim.handles] : [],
  };
}
export const EMPTY_EXTERIOR_TRIM_KAM: ExteriorTrimKAM = {
  type: "EXTERIOR_TRIM",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    profile: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultExteriorTrimKAMOptions(): ExteriorTrimKAMOptions {
  return {
    attributes: {},
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}
export const defaultExteriorTrimKAMSort = (a: ExteriorTrimKAM, b: ExteriorTrimKAM) => {
  return (
    b.attributes.type.value.localeCompare(a.attributes.type.value) ||
    a.attributes.profile.value.localeCompare(b.attributes.profile.value) ||
    a.attributes.name.value.localeCompare(b.attributes.name.value)
  );
};

/** @deprecated - use the Building Query API instead */
export function buildingExteriorTrimKAMs(building: Building): ExteriorTrimKAM[] {
  return getTrimFromBuilding(building).map(makeExteriorTrimKAM);
}

export const EXTERIOR_TRIM_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  ExteriorTrimAttributes,
  ExteriorTrimMeasures
> = {
  attributes: {
    entityName: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_EXTERIOR_TRIM: (keyof ExteriorTrimAttributes)[] = ["type", "profile", "name"];

// OPENING_CASING
/** @deprecated - use the Building Query API instead */
function getOpeningCasingFromBuilding(building: Building): OpeningTrimRail[] {
  const {
    settings: { profile },
    enabledFeatures,
    doc: { profiles },
  } = building;
  const profileTable = getActiveProfileTable({
    settingsToCatalogs: enabledFeatures.settingsToCatalogs,
    profiles,
    profileSettings: profile,
  });
  const openingCasings = getOpeningRails(building, undefined, TrimKeyMeasureCondition.EXTERIOR);

  return openingCasings.map((trim) => {
    trim.profile = trim.profileId ? profileTable[trim.profileId]?.name ?? "DEFAULT_PROFILE" : "DEFAULT_PROFILE";
    return trim;
  });
}

export function makeOpeningCasingKAM(trim: TrimKBMData): OpeningCasingKAM {
  return {
    type: "OPENING_CASING",
    attributes: {
      category: keyAttributeNew(trim.category),
      name: keyAttributeNew(trim.name),
      profile: keyAttributeNew(trim.profile ?? ""),
      entityName: keyAttributeNew(`${trim.category}-${trim.profile}-${trim.name}`),
    },
    measures: {
      length: keyMeasureNew(trim.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: trim.handles ? [...trim.handles] : [],
  };
}
export const EMPTY_OPENING_CASING_KAM: OpeningCasingKAM = {
  type: "OPENING_CASING",
  attributes: {
    category: keyAttributeNew(""),
    name: keyAttributeNew(""),
    profile: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultOpeningCasingKAMOptions(): OpeningCasingKAMOptions {
  return {
    attributes: {},
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}
export const defaultOpeningCasingKAMSort = (a: OpeningCasingKAM, b: OpeningCasingKAM) => {
  return (
    b.attributes.category.value.localeCompare(a.attributes.category.value) ||
    a.attributes.profile.value.localeCompare(b.attributes.profile.value) ||
    a.attributes.name.value.localeCompare(b.attributes.name.value)
  );
};

/** @deprecated - use the Building Query API instead */
export function buildingOpeningCasingKAMs(building: Building): OpeningCasingKAM[] {
  return getOpeningCasingFromBuilding(building).map(makeOpeningCasingKAM);
}

export const OPENING_CASING_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  OpeningCasingAttributes,
  OpeningCasingMeasures
> = {
  attributes: {
    entityName: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_OPENING_CASING: (keyof OpeningCasingAttributes)[] = ["category", "profile", "name"];
